import { ClampToEdgeWrapping, DataTexture, LinearFilter, LinearMipMapLinearFilter, RGBAFormat, ShaderMaterial, UnsignedByteType, UVMapping } from 'three';
import { defineComponent, inject, onBeforeMount, onUnmounted, provide, reactive } from 'vue';

import { simpleVs } from '@resn/gozer-three';
import { ShaderPass } from '@resn/gozer-three/passes';

import glslSnoise from '@/libs/lygia/generative/snoise.glsl';
import glslVoronoi from '@/libs/lygia/generative/voronoi.glsl';
import glslScale from '@/libs/lygia/space/scale.glsl';

const NoiseTexturesKey = 'noiseTextures';
export const useNoiseTextures = () => {
    const noiseTextures = inject(NoiseTexturesKey, {});
    return noiseTextures;
};

export const NoiseTextures = defineComponent({
    setup() {
        const { renderer } = inject('renderer');
        const props = reactive({});
        const preGeneratedTextures = {};

        const generateNoise = ({ size = 512, offset = { x: 0, y: 0 } } = {}) => {
            const pass = new ShaderPass(renderer, {
                width: size,
                height: size,
                shader: new ShaderMaterial({
                    vertexShader: simpleVs,
                    fragmentShader: /* glsl */ `
                        varying vec2 vUv;
                        uniform vec2 u_offset;
                        ${glslSnoise}
                        void main() {
                            vec2 st = vUv;
                                st += u_offset;
                            float n = snoise(st);
                            vec4 res = vec4(0.);
                                res.xyz = vec3(n);
                            gl_FragColor = res;
                        }
                    `,
                    uniforms: {
                        u_offset: { value: offset },
                    },
                    name: 'NoiseTexture',
                }),
            });

            pass.render({ final: false });

            const pixels = new Uint8Array(size * size * 4);
            renderer.readRenderTargetPixels(pass.fbo.read, 0, 0, size, size, pixels);

            const t = createDataTexture(pixels, size);
            t.needsUpdate = true;

            pass.dispose();

            return { value: t };
        };

        const generateVoronoi = ({ size = 1024, scale = 100, offset = { x: 0, y: 0 } } = {}) => {
            const pass = new ShaderPass(renderer, {
                width: size,
                height: size,
                shader: new ShaderMaterial({
                    vertexShader: simpleVs,
                    fragmentShader: /* glsl */ `
                        varying vec2 vUv;
                        uniform vec2 u_offset;
                        uniform float u_scale;
                        ${glslVoronoi}
                        ${glslScale}
                        void main() {
                            vec2 st = vUv;
                                 st = scale(st, u_scale);
                            vec4 res = vec4(0.);
                                 res.xyz = voronoi(st);
                            gl_FragColor = res;
                        }
                    `,
                    uniforms: {
                        u_offset: { value: offset },
                        u_scale: { value: scale },
                    },
                    name: 'VoronoiTexture',
                }),
            });

            pass.render({ final: false });

            const pixels = new Uint8Array(size * size * 4);
            renderer.readRenderTargetPixels(pass.fbo.read, 0, 0, size, size, pixels);

            const t = createDataTexture(pixels, size);
            t.needsUpdate = true;

            pass.dispose();

            return { value: t };
        };

        const createDataTexture = (pixels, size) => {
            return new DataTexture(pixels, size, size,
                RGBAFormat,
                UnsignedByteType,
                UVMapping,
                ClampToEdgeWrapping,
                ClampToEdgeWrapping,
                LinearFilter,
                LinearMipMapLinearFilter
            );
        };

        onBeforeMount(() => {
            preGeneratedTextures.noise = generateNoise();
            preGeneratedTextures.voronoiScale160 = generateVoronoi({ scale: 160 });
        });

        onUnmounted(() => {
            for (const key in preGeneratedTextures) {
                preGeneratedTextures[key]?.value?.dispose();
            }
        });

        provide(NoiseTexturesKey, {
            props,
            generateNoise,
            generateVoronoi,
            preGeneratedTextures,
        });
    },

    render() {
        return this.$slots.default();
    },
});
